import { EditMode } from "@/types/editMode";
import {
  AppSettingType,
  Co2MaterialCategory,
  Co2MaterialLibrary,
  Co2MaterialLibraryScope,
  Co2MaterialType,
  DensityType,
  FileParameter,
  ImportCo2MaterialTypesResponse,
  SoftDeleteStatusType,
  TemplateType,
  UpsertCo2MaterialTypeRequest,
  ValidationFailure,
} from "@api/apiClient";
import { useCo2MaterialLibraries } from "@api/co2MaterialLibraries";
import { Co2MaterialTypeQueryFilter, useCo2MaterialTypeOptions, useCo2MaterialTypesQuery, useDeleteCo2MaterialType, useUpsertCo2MaterialType } from "@api/co2MaterialTypes";
import { useUploadTemplate } from "@api/templates";
import DataGrid from "@components/DataGrid/DataGrid";
import { GridToolbarImportButton } from "@components/DataGrid/components/toolbar/GridToolbarImportButton";
import MaterialLibrarySelect from "@components/DataGrid/components/toolbar/MaterialLibrarySelect";
import MultiGridToolbarSearchFilter from "@components/DataGrid/components/toolbar/MultiGridToolbarSearchFilter";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CancelIcon from "@mui/icons-material/Cancel";
import SuccessIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import SaveIcon from "@mui/icons-material/Save";
import ResetIcon from "@mui/icons-material/SettingsBackupRestore";
import WarningIcon from "@mui/icons-material/Warning";
import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Grid, Theme, Tooltip, alpha, darken, lighten, useTheme } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridEditInputCell,
  GridEventListener,
  GridExceljsProcessInput,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridFilterModel,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridSlotsComponentsProps,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { getClientColumnConfig, getClientConfigKey, setMaterialsAsDefault, validateClientConfig } from "@utils/clientConfigUtils";
import _ from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-lodash-debounce";
import useResizeObserver from "use-resize-observer";
import DataGridAutocomplete from "./DataGridAutocomplete";

export interface MaterialGridProps {
  gridHeight?: number;
  materialLibraryScope: Co2MaterialLibraryScope;
  materialLibraryParentId?: string;
  compactLayout?: boolean;
}

import { isMaterialLibraryInScope } from "@utils/materialLibraryScopeUtils";

const MaterialGrid = observer((props: MaterialGridProps) => {
  const appStore = useAppStore();
  const theme = useTheme();
  const [editMode, setEditMode] = useState(EditMode.ReadOnly);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [showConfigErrorMessage, setShowConfigErrorMessage] = useState(false);
  const [configErrorMessage, setConfigErrorMessage] = useState<string>("");
  const [importData, setImportData] = useState<FileParameter>();
  const [showArchived, setShowArchived] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 400);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const rowModesModelRef = useRef<GridRowModesModel>({});
  const [rows, setRows] = useState<any[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<any>({});
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
    { field: "category", sort: "asc" },
    { field: "a1A2A3", sort: "asc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [rowCount, setRowCount] = useState(0);
  const [materialLibraryIds, setMaterialLibraryIds] = useState<string[]>([]);

  const [validationErrors, setValidationErrors] = useState<any>({});
  const [validationFailure, setValidationFailure] = useState<ValidationFailure[] | undefined>();
  const [hasCarbonFactorIdError, setHasCarbonFactorIdError] = useState<boolean>(false);
  const materialLibraries = useCo2MaterialLibraries();
  const scopedMaterialLibraries = useMemo(
    () =>
      materialLibraries.data?.filter((library) =>
        isMaterialLibraryInScope(library, props.materialLibraryScope, props.materialLibraryParentId),
      ) ?? [],
    [materialLibraries.data, props.materialLibraryScope, props.materialLibraryParentId],
  );

  const apiLibraryIds = useMemo(() => materialLibraryIds, [materialLibraryIds]);

  const fetchData = materialLibraryIds.length > 0;

  const serverFilters: Co2MaterialTypeQueryFilter[] = (filterModel.items || [])
    .filter((item: any) => item?.field && (item?.value !== undefined || item?.operator === "isEmpty" || item?.operator === "isNotEmpty"))
    .map((item: any) => ({
      field: item.field,
      operator: item.operator || "contains",
      value: Array.isArray(item.value) ? undefined : String(item.value ?? ""),
      values: Array.isArray(item.value) ? item.value.map((x: any) => String(x)) : undefined,
    }));

  const co2MaterialTypes = useCo2MaterialTypesQuery({
    materialLibraryIds: apiLibraryIds,
    showArchived,
    search: debouncedSearchText,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    sortField: sortModel[0]?.field,
    sortDirection: (sortModel[0]?.sort as "asc" | "desc" | undefined) ?? "asc",
    filters: serverFilters,
  }, {
    enabled: fetchData,
  });
  const co2MaterialTypeOptions = useCo2MaterialTypeOptions(apiLibraryIds, showArchived, {
    enabled: fetchData,
  });

  useEffect(() => {
    co2MaterialTypeOptions.data?.co2MaterialCategories?.sort(
      (a: Co2MaterialCategory | undefined, b: Co2MaterialCategory | undefined) => a?.name?.localeCompare(b?.name || "") || 0,
    );
  }, [co2MaterialTypeOptions.data]);

  const upsertCo2MaterialType = useUpsertCo2MaterialType();
  const deleteCo2MaterialType = useDeleteCo2MaterialType();
  const uploadTemplate = useUploadTemplate({
    onSuccess: async (response: ImportCo2MaterialTypesResponse) => {
      // TODO: must disable EDIT mode when viewing results
      const successRows =
        response.importCo2MaterialTypeResult?.successItems?.map((item, index) => ({ ...item, rowId: index, id: index, co2MaterialTypeId: item.co2MaterialTypeId || index })) || [];
      const failureRows =
        response.importCo2MaterialTypeResult?.failureItems?.map((item, index) => ({
          rowId: index + successRows.length + 1,
          id: index + successRows.length + 1,
          ...item.importCo2MaterialType,
          co2MaterialTypeId: item.importCo2MaterialType?.co2MaterialTypeId || index + successRows.length + 1,
        })) || [];
      const validationerrors = response.validationFailures;
      const newRows = [...successRows, ...failureRows];
      const hasCarbonFactorIdError = validationerrors?.some((error: ValidationFailure) => 
        error.propertyName === "CarbonFactorId"
      ) ?? false;

      setHasCarbonFactorIdError(hasCarbonFactorIdError);
      setShowSuccessMessage(failureRows.length === 0 && validationerrors != undefined && validationerrors.length == 0);
      setShowErrorMessage(failureRows.length !== 0 || hasCarbonFactorIdError);
      setShowWarningMessage(failureRows.length === 0 && validationerrors != undefined && validationerrors.length > 0 && !hasCarbonFactorIdError);
      setValidationFailure(validationerrors);
      setRows(newRows);
      setRowCount(newRows.length);
      startImportMode();
    },
  });
  const apiRef = useGridApiRef();
  const containerRef = useRef(null);
  const alertRef = useRef(null);
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;
  useResizeObserver({
    ref: containerRef,
    onResize: (size) => {
      if (editModeRef.current === EditMode.Import || props.compactLayout) {
        return;
      }
      setContainerHeight(size.height || 400);
      setGridHeight((size.height || 400) - (alertHeight || 0));
    },
  });
  const { height: alertHeight } = useResizeObserver({
    ref: alertRef,
    onResize: (size) => {
      if (editModeRef.current === EditMode.Import || props.compactLayout) {
        return;
      }
      setGridHeight(containerHeight - (size.height || 0) - 16);
    },
  });
  const [containerHeight, setContainerHeight] = useState(0);
  const [gridHeight, setGridHeight] = useState(props.gridHeight || 400);
  const [coordinates, setCoordinates] = useState({ rowIndex: 0, colIndex: 0 });
  const [densityIndicator] = useState([
    { value: DensityType.Volume, name: i18n.t("material-grid.density-type.volume") },
    { value: DensityType.Area, name: i18n.t("material-grid.density-type.area") },
  ]);
  const startImportMode = () => {
    const defaultImportVisibility = {
      ...columnVisibilityModel,
      rowNum: true,
      importStatus: true,
      importAction: true,
      importStatusMessage: true,
      actions: false, // Hide actions during import
    };
    setColumnVisibilityModel(defaultImportVisibility);
  };

  const endImportMode = async () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setShowWarningMessage(false);
    setShowConfigErrorMessage(false);
    setHasCarbonFactorIdError(false);
    setValidationFailure([]);
    setRows([]);
    setRowCount(0);
    setImportData(undefined);
    setMaterialLibraryIds([]);
    setGridHeight(containerHeight > 0 ? containerHeight - 16 : props.gridHeight || 400);
    setEditMode(EditMode.ReadOnly);

    // Reset column visibility to initial state instead of manually setting each column
    if (defaultColumns.length > 0) {
      const resetColumnVisibility = getInitialColumnVisibilityModel();

      setColumnVisibilityModel(resetColumnVisibility);

      setDisplayColumns(defaultColumns);
      const sourceCol = defaultColumns.find((c) => c.field === "source" ) as any;
      if (sourceCol) {
        const updatedColumns = defaultColumns.map((col) =>
          col.field === "source"
            ? {
                ...col,
                valueGetter: (value: any,row:any) => {
                  console.log("Getting value for source column", { value, row });
                  return value;
                },
              }
            : col,
        );
        setDisplayColumns(updatedColumns);
      }

    }
  };

  useEffect(() => {
    setMaterialLibraryIds([]);
    setRows([]);
    setRowCount(0);
    setEditMode(EditMode.ReadOnly);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setShowWarningMessage(false);
    setValidationFailure([]);
  }, [props.materialLibraryScope, props.materialLibraryParentId]);

  const canEdit = editMode !== EditMode.Import;
  const isImportPreview = editMode === EditMode.Import;
  const gridRows = isImportPreview ? rows : fetchData ? rows : [];
  const gridRowCount = isImportPreview ? rows.length : fetchData ? rowCount : 0;

  useEffect(() => {
    apiRef.current.scrollToIndexes(coordinates);
    apiRef.current.setCellFocus(coordinates.rowIndex, "name");
  }, [apiRef, coordinates]);

  useEffect(() => {
    if (editMode === EditMode.Import) {
      return;
    }

    if (!fetchData || scopedMaterialLibraries.length === 0 || materialLibraryIds.length === 0) {
      setRows([]);
      setRowCount(0);
      return;
    }

    if (co2MaterialTypes.data) {
      setRowCount(co2MaterialTypes.data.totalCount ?? 0);
      const offset = paginationModel.page * paginationModel.pageSize;
      const newRows = (co2MaterialTypes.data.items || [])
        .filter((r: Co2MaterialType) => showArchived || r.deleteStatus === SoftDeleteStatusType.Active)
        .map((r: Co2MaterialType, index: number) => {
          return { ...r, rowId: offset + index, id: r.co2MaterialTypeId ?? index };
        });
      setRows(newRows);
    } else {
      // Clear rows when data is not available (e.g., during loading)
      setRows([]);
      setRowCount(0);
    }
  }, [fetchData, co2MaterialTypes.data, showArchived, scopedMaterialLibraries, materialLibraryIds, editMode, paginationModel.page, paginationModel.pageSize]);

  // Keep the ref in sync with the state
  useEffect(() => {
    rowModesModelRef.current = rowModesModel;
  }, [rowModesModel]);

  type ExtendedGridColDef = {
    hidden?: boolean;
    export?: boolean;
  } & GridColDef;

  const getGridRenderErrors = useCallback(
    (params: GridRenderEditCellParams, field?: string): boolean | undefined => {
      const errors: any = params.error || validationErrors[params.id]?.errors[field || params.field]?.join(" ");
      return errors ?? false;
    },
    [validationErrors],
  );

  const getGridPreProcessErrors = useCallback(
    (params: GridPreProcessEditCellProps, field?: string): boolean | string | undefined => {
      const errors: any = params.props.error || validationErrors[params.id]?.errors[field || params.props.field]?.join(" ");
      return errors;
    },
    [validationErrors],
  );

  const [defaultColumns, setDefaultColumns] = useState<ExtendedGridColDef[]>([]);
  const [displayColumns, setDisplayColumns] = useState<ExtendedGridColDef[]>([]);

  useEffect(() => {
    const clientKey = getClientConfigKey(appStore.applicationSettings);
    const config = getClientColumnConfig(clientKey);

    if (!config) {
      setConfigErrorMessage(`Failed to load column configuration for client: ${clientKey}`);
      setShowConfigErrorMessage(true);
      return;
    }

    // Validate configuration
    const validation = validateClientConfig(clientKey);
    if (!validation.isValid) {
      setConfigErrorMessage(`Invalid configuration for client ${clientKey}: ${validation.errors.join(", ")}`);
      setShowConfigErrorMessage(true);
      return;
    }

    if (validation.warnings.length > 0) {
      console.warn(`Configuration warnings for client ${clientKey}:`, validation.warnings);
    }

    // Clear any previous configuration errors
    setShowConfigErrorMessage(false);
    setConfigErrorMessage("");

    const baseColumns = config.columns || [];

    const columns = baseColumns.map((col: any) => getColumnByField(col)).filter(Boolean) as ExtendedGridColDef[];

    setDefaultColumns(columns);
    setDisplayColumns(columns); // Initially, display columns same as default columns

    const sourceCol = columns.find((c) => c.field === "source" ) as any;
      if (sourceCol) {
        const updatedColumns = columns.map((col) =>
          col.field === "source"
            ? {
                ...col,
                valueGetter: (value: any,row:any) => {
                  console.log("Getting value for source column", { value, row });
                  return value;
                },
              }
            : col,
        );
        setDisplayColumns(updatedColumns);
      }

  }, [appStore.features]);

  const getI18nText = useCallback(
    (headerNameKey: string, descriptionKey: string) => ({
      headerName: i18n.t(`${headerNameKey}`),
      description: i18n.t(`${descriptionKey}`),
      placeholder: i18n.t(`${headerNameKey}`),
    }),
    [],
  );

  const getColumnByField = useCallback(
    (col: any): ExtendedGridColDef | undefined => {
      const i18nText = getI18nText(col.headerNameKey, col.descriptionKey);
      const baseColumn: Partial<ExtendedGridColDef> = {
        field: col.field,
        ...i18nText,
        flex: col.flex || 1,
        minWidth: col.minWidth,
        editable: col.editable !== undefined ? col.editable && canEdit : canEdit,
        hidden: col.hidden,
        export: col.export !== undefined ? col.export : true,
      };

      // Handle sourceProperty columns first
      if (col.sourceProperty && col.sourceProperty.trim() !== "") {
        return {
          ...baseColumn,
          type: col.type || "string",
          valueGetter: (_value: any, row: any) => {
            try {
              if (row.sourceData != undefined) {
                return col.sourceProperty.split(".").reduce((acc: any, key: string) => acc?.[key], row.sourceData);
              }
              return "";
            } catch {
              return "";
            }
          },
        } as ExtendedGridColDef;
      }

      // Column type factories
      const columnFactories: Record<string, () => ExtendedGridColDef> = {
        // Special columns with custom logic
        rowNum: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "number",
            renderCell: (params: any) => +params.row.rowId + 1,
          }) as ExtendedGridColDef,

        importStatus: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "custom",
            align: "center" as const,
            renderCell: (params: any) => (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                {params.row.importStatus === "Error" && <ErrorIcon sx={{ color: theme.palette.error.light }} />}
                {params.row.importStatus === "Warning" && <WarningIcon sx={{ color: theme.palette.warning.light }} />}
                {params.row.importStatus === "Success" && <SuccessIcon sx={{ color: theme.palette.success.light }} />}
              </Box>
            ),
          }) as ExtendedGridColDef,

        // Standard string columns with validation
        name: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "string",
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
              ...params.props,
              error: _.isNil(params.props.value),
            }),
            renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
          }) as ExtendedGridColDef,

        // Autocomplete columns
        category: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "category-select",
            label: "Category",
            options: co2MaterialTypeOptions?.data?.co2MaterialCategories || [],
            getValue: (row: any) =>
              co2MaterialTypeOptions?.data?.co2MaterialCategories?.find(
                (x) => x.co2MaterialCategoryId === row.category?.co2MaterialLibraryId || x.co2MaterialCategoryId === row.co2MaterialCategory?.co2MaterialCategoryId,
              ) || null,
            errorField: "co2MaterialCategoryId",
            freeSolo: true,
            valueGetter: (_value: any, row: any) => row.co2MaterialCategory?.name || row.co2MaterialCategoryName || "",
          }),

        unit: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "unit-select",
            label: "Units",
            options: co2MaterialTypeOptions?.data?.units || [],
            getValue: (row: any) => co2MaterialTypeOptions?.data?.units?.find((x) => x.value === row.unit) || null,
            freeSolo: false,
          }),

        library: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "library-select",
            label: "Material Library",
            options: co2MaterialTypeOptions?.data?.co2MaterialLibraries || [],
            getValue: (row: any) =>
              co2MaterialTypeOptions?.data?.co2MaterialLibraries?.find(
                (x) => x.co2MaterialLibraryId === row.library?.co2MaterialLibraryId || x.co2MaterialLibraryId === row.co2MaterialLibrary?.co2MaterialLibraryId,
              ) || null,
            errorField: "co2MaterialLibraryId",
            freeSolo: true,
            valueGetter: (_value: any, row: any) => row.co2MaterialLibrary?.name || row.co2MaterialLibraryName || "",
          }),

        densityType: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "density-indicator-select",
            label: i18n.t("material-grid.columns.auto-complete-labels.density-type"),
            options: densityIndicator,
            getValue: (row: any) => row.densityType || null,
            freeSolo: false,
          }),

        // Date columns
        createdAt: () => createDateColumn(baseColumn),
        modifiedAt: () => createDateColumn(baseColumn),

        // String columns with pattern validation
        masterFormatNumber: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "string",
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
              const pattern = /^\d\d \d\d \d\d$/;
              const isValid = _.isNil(params.props.value) || pattern.test(params.props.value);
              return { ...params.props, error: !isValid };
            },
            renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
          }) as ExtendedGridColDef,

        // Simple string columns
        co2MaterialTypeId: () => createStringColumn(baseColumn, i18nText, true),
        co2MaterialCategoryId: () => createStringColumn(baseColumn, i18nText),
        co2MaterialLibraryId: () => createStringColumn(baseColumn, i18nText),

        deleteStatus: (): ExtendedGridColDef => ({ ...baseColumn, type: "string" }) as ExtendedGridColDef,

        // Actions column
        actions: (): ExtendedGridColDef =>
          ({
            ...i18nText,
            field: "actions",
            type: "actions",
            resizable: false,
            width: 100,
            hidden: col.hidden,
            export: col.export ?? false,
            getActions: ({ id, row }: any) => {
              const isInEditMode = rowModesModelRef.current[id]?.mode === GridRowModes.Edit;
              if (isInEditMode) {
                return [
                  <GridActionsCellItem
                    key={`save-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.save-button.tooltip")}>
                        <SaveIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.save-button.label")}
                    onClick={() => handleSaveClick(id)}
                    sx={{ color: "primary.main" }}
                  />,
                  <GridActionsCellItem
                    key={`cancel-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.cancel-button.tooltip")}>
                        <CancelIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.cancel-button.label")}
                    onClick={() => handleCancelClick(id)}
                  />,
                ];
              } else if (row.status === "Deleted") {
                return [
                  <GridActionsCellItem
                    key={`restore-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.restore-button.tooltip")}>
                        <ResetIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.restore-button.label")}
                    onClick={() => handleRestoreClick(id, row)}
                  />,
                ];
              } else {
                return [
                  <GridActionsCellItem
                    key={`edit-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.edit-button.tooltip")}>
                        <EditIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.edit-button.label")}
                    onClick={() => handleEditClick(id)}
                  />,
                  <GridActionsCellItem
                    key={`delete-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.delete-button.tooltip")}>
                        <DeleteIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.delete-button.label")}
                    onClick={() => handleDeleteClick(id, row)}
                  />,
                ];
              }
            },
          }) as ExtendedGridColDef,
      };

      // Get the factory function for this column type
      const factory = columnFactories[col.field];

      if (factory) {
        return factory();
      }

      // For any other numeric columns (like b1, b2, c1, c2, etc.)
      if (col.type === "number") {
        return createNumberColumn(baseColumn, i18nText);
      }

      // Default to string column
      return createStringColumn(baseColumn, i18nText);
    },
    [canEdit, co2MaterialTypeOptions?.data, densityIndicator, theme, validationErrors, rowModesModelRef, getI18nText],
  );

  // Helper functions for column creation
  const createNumberColumn = useCallback(
    (baseColumn: Partial<ExtendedGridColDef>, i18nText: any): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "number",
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
          ...params.props,
          error: _.lt(params.props.value, 0) || !_.isFinite(params.props.value),
        }),
        renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
        valueFormatter: (value: any) => {
          if (value === undefined || value === null) return "";
          return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
        },
      }) as ExtendedGridColDef,
    [getGridRenderErrors],
  );

  const createStringColumn = useCallback(
    (baseColumn: Partial<ExtendedGridColDef>, i18nText: any, requiresValidation = false): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "string",
        ...(requiresValidation && {
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
            ...params.props,
            error: _.isNil(params.props.value),
          }),
        }),
        renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
      }) as ExtendedGridColDef,
    [getGridRenderErrors],
  );

  const createAutocompleteColumn = useCallback(
    (
      baseColumn: Partial<ExtendedGridColDef>,
      i18nText: any,
      options: {
        id: string;
        label: string;
        options: any[];
        getValue: (row: any) => any;
        errorField?: string;
        freeSolo?: boolean;
        valueGetter?: (value: any, row: any) => any;
      },
    ): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "string",
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
          ...params.props,
          error: _.isNil(params.props.value),
        }),
        ...(options.valueGetter && { valueGetter: options.valueGetter }),
        renderEditCell: (params: GridRenderEditCellParams) => {
          const { id, field, row } = params;
          const onChange = async (_event: any, newValue: any) => {
            await params.api.setEditCellValue({ id, field, value: newValue });
            params.api.stopCellEditMode({ id, field });
          };
          return (
            <DataGridAutocomplete
              id={options.id}
              label={options.label}
              rows={options.options}
              value={options.getValue(row)}
              onChange={onChange}
              hasFocus={params.hasFocus}
              error={getGridRenderErrors(params, options.errorField)}
              placeholder={i18nText.placeholder}
              freeSolo={options.freeSolo !== false}
            />
          );
        },
      }) as ExtendedGridColDef,
    [getGridRenderErrors],
  );

  const createDateColumn = useCallback(
    (baseColumn: Partial<ExtendedGridColDef>): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "dateTime",
        editable: false,
        renderCell: (params: any) => {
          const date = params.row[baseColumn.field!] ? new Date(params.row[baseColumn.field!]) : new Date();
          return i18n.t("datetime_short", { value: date });
        },
      }) as ExtendedGridColDef,
    [],
  );

  const fieldsToExport = useMemo(() => {
    return displayColumns.filter((col) => col.export !== false).map((col) => col.field);
  }, [displayColumns]);

  // Helper function to generate initial column visibility model
  const getInitialColumnVisibilityModel = () => {
    if (defaultColumns.length === 0) {
      return {
        rowNum: false,
        importStatus: false,
        importAction: false,
        importStatusMessage: false,
      };
    }

    const baseVisibilityModel = defaultColumns.reduce((acc: any, column: any) => {
      if (column.hidden) {
        acc[column.field] = false;
      }
      return acc;
    }, {});

    // Always hide import-related columns by default
    baseVisibilityModel.rowNum = false;
    baseVisibilityModel.importStatus = false;
    baseVisibilityModel.importAction = false;
    baseVisibilityModel.importStatusMessage = false;

    return baseVisibilityModel;
  };

  // Initialize column visibility whenever defaultColumns changes
  useEffect(() => {
    if (defaultColumns.length > 0) {
      const columnVisibilityModel = getInitialColumnVisibilityModel();
      setColumnVisibilityModel(columnVisibilityModel);
      setDisplayColumns(defaultColumns); // Ensure display columns are in sync
      const sourceCol = defaultColumns.find((c) => c.field === "source" ) as any;
      if (sourceCol) {
        const updatedColumns = defaultColumns.map((col) =>
          col.field === "source"
            ? {
                ...col,
                valueGetter: (value: any,row:any) => {
                  console.log("Getting value for source column", { value, row });
                  return value;
                },
              }
            : col,
        );
        setDisplayColumns(updatedColumns);
      }
    }
  }, [defaultColumns]);

  // Ensure actions column is visible for default clients (not during import mode)
  useEffect(() => {
    if (defaultColumns.length > 0 && editMode !== EditMode.Import) {
      const actions = defaultColumns.find((col) => col.field === "actions")?.hidden === false ? true : false;
      setColumnVisibilityModel((prev: any) => ({
        ...prev,
        actions,
      }));
    }
  }, [defaultColumns, editMode, appStore.features]);

  // Cleanup effect to reset import mode when component unmounts
  useEffect(() => {
    return () => {
      // Reset any import mode state when component unmounts
      setEditMode(EditMode.ReadOnly);
      setShowSuccessMessage(false);
      setShowErrorMessage(false);
      setShowWarningMessage(false);
      setShowConfigErrorMessage(false);
      setHasCarbonFactorIdError(false);
    };
  }, []);

  const handleAddRow = () => {
    // TODO: create instance of Co2MaterialType for HTTP Post/Create
    const co2MaterialTypeItem: any = {
      /* TODO */
      id: -1,
      co2MaterialTypeId: -1,
      name: "",
      description: "",
      // TODO: these should be nullable now
      a1A2A3: null,
      a1A2A3_ECF: null,
      a4: null,
      a4_ECF: null,
      a5: null,
      a5_ECF: null,
      // TODO: add new fields
      unit: "m3",
      unitQuantity: 1,
      co2MaterialLibrary: null,
      massPerUnit: 0,
      density: 0,
      source: "",
      co2MaterialCategory: {
        name: null,
      },
    };
    //setRowModesModel({ ...rowModesModel, [-1]: { mode: GridRowModes.Edit } });
    setRows((oldRows: any) => [{ ...co2MaterialTypeItem, isNew: true }, ...oldRows]);
    setEditMode(EditMode.Add);
    setTimeout(() => {
      apiRef.current.startRowEditMode({ id: -1 });
      setTimeout(() => {
        setCoordinates({ rowIndex: 0, colIndex: 0 });
      }, 1000);
    });
  };

  const handleShowArchived = (showArchived: boolean) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setShowArchived(showArchived);
  };

  const handleEditClick = async (id: GridRowId) => {
    apiRef.current.startRowEditMode({ id });
    const newRowModesModel = { ...rowModesModel, [id]: { mode: GridRowModes.Edit } };
    setRowModesModel(newRowModesModel);
    rowModesModelRef.current = newRowModesModel;
  };

  const handleDeleteClick = async (id: GridRowId, _row: any) => {
    // TODO: handle delete material
    await deleteCo2MaterialType.mutateAsync(id as any);
  };

  const handleRestoreClick = async (_id: GridRowId, _row: any) => {
    // TODO: handle restore material
  };

  const handleSaveClick = async (id: GridRowId) => {
    setEditMode(EditMode.ReadOnly);
    const newRowModesModel = { ...rowModesModel, [id]: { mode: GridRowModes.View } };
    setRowModesModel(newRowModesModel);
    rowModesModelRef.current = newRowModesModel;
  };

  const handleCancelClick = (id: GridRowId) => {
    setEditMode(EditMode.ReadOnly);
    const newRowModesModel = { ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } };
    setRowModesModel(newRowModesModel);
    rowModesModelRef.current = newRowModesModel;

    const newRows = rows.filter((r: any) => r.isNew !== true);
    setRows(newRows);
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
    rowModesModelRef.current = newRowModesModel;
  };

  const handleCellEditStart: GridEventListener<"cellEditStart"> = (_params, _event) => {
    setEditMode(EditMode.Edit);
  };

  const handleCellEditStop: GridEventListener<"cellEditStop"> = (_params, _event) => {
    setEditMode(EditMode.ReadOnly);
  };

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (_params, _event) => {
    setEditMode(EditMode.Edit);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }

    const pendingChanges = rows.some((r: any) => r.isNew === true);
    setEditMode(pendingChanges ? EditMode.Add : EditMode.ReadOnly); // ??
  };

  const getCo2MaterialCategoryId = (row: GridRowModel): string | undefined => {
    if (typeof row.category === "object") {
      return row.category?.co2MaterialCategoryId;
    }
    if (typeof row.category === "string") {
      if (row.category === row.co2MaterialCategory?.name) {
        return row.co2MaterialCategory?.co2MaterialCategoryId;
      }
    }
    return undefined;
  };

  const getCo2MaterialCategoryName = (row: GridRowModel): string | undefined => {
    // User has imported a custom library name from excel
    if (typeof row.co2MaterialCategory === "string") {
      return row.co2MaterialCategory;
    }
    // User has entered a custom library name in the grid editor and is not one of the existing libraries
    if (typeof row.category === "string") {
      if (row.category === row.co2MaterialCategory?.name) {
        return undefined;
      }
      return row.category;
    }
    return undefined;
  };

  const getCo2MaterialLibraryId = (row: GridRowModel): string | undefined => {
    if (typeof row.library === "object") {
      return row.library?.co2MaterialLibraryId;
    }
    if (typeof row.library === "string") {
      if (row.library === row.co2MaterialLibrary?.name) {
        return row.co2MaterialLibrary?.co2MaterialLibraryId;
      }
    }
    return undefined;
  };

  const getCo2MaterialLibraryName = (row: GridRowModel): string | undefined => {
    // User has imported a custom library name from excel
    if (typeof row.co2MaterialLibrary === "string") {
      return row.co2MaterialLibrary;
    }
    // User has entered a custom library name in the grid editor and is not one of the existing libraries
    if (typeof row.library === "string") {
      if (row.library === row.co2MaterialLibrary?.name) {
        return undefined;
      }
      return row.library;
    }
    return undefined;
  };

  const processRowUpdate = async (newRow: GridRowModel, _oldRow: GridRowModel) => {
    // TODO: here we can validate the row and return null if it is not valid

    if (newRow.ignoreModifications) {
      return newRow;
    }
    const createCo2MaterialTypeRequest = new UpsertCo2MaterialTypeRequest({
      co2MaterialTypeId: newRow.id === -1 ? undefined : newRow.co2MaterialTypeId,
      name: newRow.name,
      description: newRow.description,
      a1A2A3: +newRow.a1A2A3,
      a4: +newRow.a4,
      a5: +newRow.a5,
      b1: +newRow.b1,
      b2: +newRow.b2,
      b3: +newRow.b3,
      b4: +newRow.b4,
      b5: +newRow.b5,
      b6: +newRow.b6,
      b7: +newRow.b7,
      c1: +newRow.c1,
      c2: +newRow.c2,
      c3: +newRow.c3,
      c4: +newRow.c4,
      d: +newRow.d,
      biogenic: +newRow.biogenic,
      wastage: +newRow.wastage,
      unit: newRow.unit?.value ?? newRow.unit,
      co2MaterialLibraryId: getCo2MaterialLibraryId(newRow),
      co2MaterialLibraryName: getCo2MaterialLibraryName(newRow),
      unitQuantity: +newRow.unitQuantity,
      massPerUnit: +newRow.massPerUnit,
      density: +newRow.density,
      source: newRow.source,
      densityType: newRow.densityType?.value,
      co2MaterialCategoryId: getCo2MaterialCategoryId(newRow),
      co2MaterialCategoryName: getCo2MaterialCategoryName(newRow),
    });
    const response: any = await upsertCo2MaterialType.mutateAsync([createCo2MaterialTypeRequest] as any);
    if (response.failureItems.length > 0) {
      const errors = { [-1]: { errors: response.failureItems[0].errors } };
      setValidationErrors(errors);
      throw new Error("Validation error");
    } else {
      setRows((oldRows: any) => oldRows.filter((r: any) => r.isNew !== true));
    }

    return newRow;
  };

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [debouncedSearchText]);

  const handleSearchTextChange = (searchText: string | undefined) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSearchText(searchText || "");
  };

  const handleMaterialLibrarySelectChange = (newSelections: string[]) => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setMaterialLibraryIds(newSelections);
  };

  const handleFileImport = async (files: File[]) => {
    const data = await files[0].arrayBuffer();
    const blob = new Blob([new Uint8Array(data)], { type: files[0].type });
    setImportData({ data: blob, fileName: files[0].name });
    setEditMode(EditMode.Import);
    await uploadTemplate.mutateAsync({
      templateType: TemplateType.Co2MaterialTypes,
      commitChanges: false,
      fileParameter: { data: blob, fileName: files[0].name },
      isDefault: setMaterialsAsDefault(appStore.applicationSettings),
      scope: props.materialLibraryScope,
      co2MaterialLibraryParentId: props.materialLibraryParentId,
    } as any);
  };

  const handleFileImportCommit = async () => {
    await uploadTemplate.mutateAsync({
      templateType: TemplateType.Co2MaterialTypes,
      commitChanges: true,
      commitAfterWarning: false,
      fileParameter: importData,
      isDefault: setMaterialsAsDefault(appStore.applicationSettings),
      scope: props.materialLibraryScope,
      co2MaterialLibraryParentId: props.materialLibraryParentId,
    } as any);

    await endImportMode();
  };

  const handleFileImportCommitPostWarnig = async () => {
    await uploadTemplate.mutateAsync({
      templateType: TemplateType.Co2MaterialTypes,
      commitChanges: false,
      commitAfterWarning: true,
      fileParameter: importData,
      isDefault: setMaterialsAsDefault(appStore.applicationSettings),
      scope: props.materialLibraryScope,
      co2MaterialLibraryParentId: props.materialLibraryParentId,
    } as any);

    await endImportMode();
  };

  const handleAbortFileImport = async () => {
    await endImportMode();
  };

  const handleDismissConfigError = () => {
    setShowConfigErrorMessage(false);
    setConfigErrorMessage("");
  };

  const exceljsPreProcess = async (_input: GridExceljsProcessInput) => {
  };
  const exceljsPostProcess = async (_input: GridExceljsProcessInput) => {
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: isImportPreview || props.compactLayout ? "auto" : "100%",
        alignSelf: isImportPreview || props.compactLayout ? "flex-start" : "stretch",
      }}
    >
      {showSuccessMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="success"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.success.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>{i18n.t("material-grid.validate-success-title")}</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{i18n.t("materials-grid.validate-success-message")}</Grid>
              <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="outlined" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleFileImportCommit} loading={uploadTemplate.isPending} disabled={uploadTemplate.isPending}>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}
      {showErrorMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="error"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.error.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>{hasCarbonFactorIdError ? "Duplicate Carbon ID Found" : i18n.t("material-grid.error-title")}</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{hasCarbonFactorIdError ? "Duplicate Carbon Factor IDs have been detected. Please ensure all Carbon Factor IDs are unique." : i18n.t("materials-grid.error-message")}</Grid>
              <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" disabled>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}

      {showWarningMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="warning"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.error.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>{i18n.t("material-grid.warning-title")}</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{i18n.t("materials-grid.warning-message")}</Grid>
              <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="outlined" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleFileImportCommitPostWarnig} loading={uploadTemplate.isPending} disabled={uploadTemplate.isPending}>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="auto">
                <ul>{validationFailure?.map((d, index) => <li key={`${index}`}>{i18n.t(`materials-grid.${d.propertyName}.${d.errorCode}`)}</li>)}</ul>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}

      {showConfigErrorMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="error"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.error.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>Configuration Error</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{configErrorMessage}</Grid>
              <Grid size="auto">
                <Button variant="outlined" color="primary" onClick={handleDismissConfigError}>
                  Dismiss
                </Button>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}

      <Box sx={{ display: "flex", flexGrow: isImportPreview ? 0 : 1, flexShrink: 0, width: "100%" }}>
        <DataGrid
          apiRef={apiRef}
          loading={(isImportPreview && uploadTemplate.isPending) || (fetchData && co2MaterialTypes.isFetching) || uploadTemplate.isPending}
          columns={displayColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          rows={gridRows}
          getRowId={(row: any) => row.co2MaterialTypeId ?? row.id ?? row.rowId}
          hideFooter={false}
          autoHeight={isImportPreview}
          pagination
          paginationMode={isImportPreview ? "client" : "server"}
          sortingMode={isImportPreview ? "client" : "server"}
          filterMode={isImportPreview ? "client" : "server"}
          rowCount={gridRowCount}
          paginationModel={paginationModel}
          pageSizeOptions={[25, 50, 100, 200]}
          onPaginationModelChange={(nextModel: any) => setPaginationModel(nextModel)}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onCellEditStart={handleCellEditStart}
          onCellEditStop={handleCellEditStop}
          onRowEditStart={handleRowEditStart}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => console.error("onProcessRowUpdateError: ", error)}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(newSortModel);
          }}
          filterModel={filterModel}
          onFilterModelChange={(nextFilterModel) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setFilterModel(nextFilterModel);
          }}
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: {
              editMode,
              showArchived,
              searchText,
              materialLibraryScope: props.materialLibraryScope,
              materialLibraryParentId: props.materialLibraryParentId,
              materialLibraryOptions: scopedMaterialLibraries,
              onAddRow: handleAddRow,
              onShowArchived: handleShowArchived,
              onSearchTextChange: handleSearchTextChange,
              onMaterialLibrarySelectChange: handleMaterialLibrarySelectChange,
              onFileImport: handleFileImport,
              excelOptions: {
                fields: fieldsToExport,
                exceljsPreProcess,
                exceljsPostProcess,
              },
            },
          }}
          initialState={{
            density: "compact",
            columns: {
              columnVisibilityModel: getInitialColumnVisibilityModel(),
            },
            sorting: { sortModel },
            pinnedColumns: { right: ["actions"] },
          }}
          sx={isImportPreview ? { width: "100%" } : { height: `${gridHeight - 16}px` }}
        />
      </Box>
    </Box>
  );
});

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    name: string;
    showArchived: boolean;
    editMode: EditMode;
    searchText: string | undefined;
    materialLibraryScope: Co2MaterialLibraryScope;
    materialLibraryParentId?: string;
    materialLibraryOptions?: Co2MaterialLibrary[];
    onAddRow: () => void;
    onShowArchived: (showArchived: boolean) => void;
    onSearchTextChange: (searchText: string | undefined) => void;
    onMaterialLibrarySelectChange: (newSelections: string[]) => void;
    onFileImport: (files: File[]) => Promise<void>;
  }
}

const EditToolbar = (props: NonNullable<GridSlotsComponentsProps["toolbar"]>) => {
  const appStore = useAppStore();
  const theme = useTheme();
  const [selectedMaterialLibraries, setSelectedMaterialLibraries] = useState<Co2MaterialLibrary[]>([]);

  const materialLibraries = useCo2MaterialLibraries();
  const clientKey = getClientConfigKey(appStore.applicationSettings);

  useEffect(() => {
    if (props.editMode === EditMode.Import) {
      return;
    }

    setSelectedMaterialLibraries([]);
    props.onMaterialLibrarySelectChange?.([]);
  }, [materialLibraries.data, props.materialLibraryScope, props.materialLibraryParentId, props.materialLibraryOptions, props.editMode]);

  const handleSearch = (searchText: string | undefined) => {
    if (props.onSearchTextChange) {
      props.onSearchTextChange(searchText);
    }
  };

  const handleMaterialLibrarySelectChange = async (_event: any, newSelections: Co2MaterialLibrary[]) => {
    setSelectedMaterialLibraries(newSelections);
    const materialLibraryIds = newSelections.map((library: Co2MaterialLibrary) => library.co2MaterialLibraryId!);
    props.onMaterialLibrarySelectChange?.(materialLibraryIds);
  };

  return (
    <GridToolbarContainer
      sx={{
        borderBottom: 1,
        backgroundColor: getBackgroundColor(theme),
        borderColor: getBorderColor(theme),
        fontWeight: "bold",
      }}
    >
      <Grid container spacing={2} sx={{ margin: 0, width: "100%" }}>
        <Grid size={6} sx={{ display: "flex", alignContent: "center", justifyContent: "start", paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
          <MultiGridToolbarSearchFilter onChange={handleSearch} value={props.searchText} />
        </Grid>
        <Grid size={6} sx={{ display: "flex", alignContent: "center", justifyContent: "end", paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
          <MaterialLibrarySelect
            options={props.materialLibraryOptions ?? []}
            value={selectedMaterialLibraries}
            showScopeIndicator={props.materialLibraryScope === Co2MaterialLibraryScope.Project}
            onChange={handleMaterialLibrarySelectChange}
            disabled={props.editMode !== EditMode.ReadOnly}
          />
        </Grid>
        <Grid size={12} sx={{ display: "flex", paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
          {clientKey !== "ADStages" && (
            <Tooltip title={i18n.t("materials-tab.grid.add-button.tooltip")}>
              <Button variant="outlined" onClick={() => props.onAddRow?.()} disabled={props.editMode !== EditMode.ReadOnly}>
                {i18n.t("materials-tab.grid.add-button.label")}
              </Button>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box>
            {clientKey !== "ADStages" && (
              <FormControlLabel
                control={
                  <Checkbox checked={props.showArchived} onChange={(_, checked) => props.onShowArchived?.(checked)} size="small" disabled={props.editMode !== EditMode.ReadOnly} />
                }
                label={i18n.t("materials-tab.grid.show-archived")}
                slotProps={{
                  typography: { variant: "body2" },
                }}
              />
            )}
          </Box>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarImportButton onFileImport={props.onFileImport} />
          <GridToolbarExport csvOptions={{ disableToolbarButton: true }} printOptions={{ disableToolbarButton: true }} />
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
};

const getBorderColor = (theme: any) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.background.default, 1), 0.88);
  }
  return darken(alpha(theme.palette.background.default, 1), 0.68);
};

const getBackgroundColor = (theme: Theme) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.divider, 1), 0.92);
  }
  return darken(alpha(theme.palette.divider, 1), 0.82);
};

export default MaterialGrid;
